import React, { useEffect, useState } from 'react';
import { CHAPTER_IDS } from '@shared/chapters';
import {
  Loader2,
  ArrowLeft,
  Save,
  CheckCircle,
  Send,
  RotateCcw,
  AlertTriangle,
  X,
  Eye,
  Edit3,
  Clock,
  Globe,
  Image as ImageIcon,
  MessageSquare,
  History,
  Smartphone,
  Wand2,
  Download,
  RotateCw,
} from 'lucide-react';
import { adminFetch } from '../../lib/api';
import {
  QAStatusBadge,
  TranslationDot,
  TranslationBadge,
  ChangeMarkerBadge,
  CHANGE_TYPE_ICONS,
} from './QAStatusBadge';
import type { QAStatus, TranslationStatusType } from './QAStatusBadge';

const CHAPTERS = CHAPTER_IDS;
const DIFFICULTIES = ['Leicht', 'Mittel', 'Schwer'];
const TYPES = ['Single Choice', 'Multiple Choice', 'Situation'];
const LANG_INFO: Record<string, { flag: string; label: string }> = {
  de: { flag: '🇩🇪', label: 'Deutsch (Master)' },
  en: { flag: '🇬🇧', label: 'English' },
  tr: { flag: '🇹🇷', label: 'Türkçe' },
  ar: { flag: '🇸🇦', label: 'العربية' },
  prs: { flag: '🇦🇫', label: 'دری' },
  ur: { flag: '🇵🇰', label: 'اردو' },
  uk: { flag: '🇺🇦', label: 'Українська' },
};

type Tab = 'content' | 'translations' | 'image' | 'history' | 'preview';

interface QuestionDetail {
  question: any;
  qa: any;
  translations: any[];
  versions: any[];
  notes: any[];
}

export const QAQuestionDetail: React.FC<{
  questionId: string;
  onBack: () => void;
  onNavigate?: (id: string) => void;
}> = ({ questionId, onBack, onNavigate }) => {
  const [data, setData] = useState<QuestionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('content');
  const [editedQ, setEditedQ] = useState<any>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [newNote, setNewNote] = useState('');
  const [showRollbackModal, setShowRollbackModal] = useState(false);

  const loadQuestion = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const d = await resp.json();
      setData(d);
      setEditedQ({ ...d.question });
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestion();
  }, [questionId]);

  const handleSave = async () => {
    if (!editedQ) return;
    setSaving(true);
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}`, {
        method: 'PUT',
        body: JSON.stringify(editedQ),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      if (result.versioned === false) {
        setMessage({ text: 'Keine Änderungen erkannt.', type: 'success' });
      } else {
        setMessage({
          text: `Gespeichert (v${result.version}). Geändert: ${result.changedFields?.join(', ')}`,
          type: 'success',
        });
      }
      await loadQuestion();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}/approve`, {
        method: 'POST',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setMessage({ text: 'Frage freigegeben ✅', type: 'success' });
      await loadQuestion();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    }
  };

  const handlePublish = async () => {
    if (!confirm('Frage live schalten? Die Änderung ist sofort für alle Nutzer sichtbar.')) return;
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}/publish`, {
        method: 'POST',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setMessage({ text: 'Frage ist jetzt LIVE 🚀', type: 'success' });
      await loadQuestion();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    }
  };

  const handleRollback = async (versionNumber: number) => {
    if (!confirm(`Auf Version ${versionNumber} zurücksetzen? Aktuelle Änderungen gehen verloren.`))
      return;
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ version_number: versionNumber }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setMessage({ text: `Rollback auf v${versionNumber} erfolgreich ⏪`, type: 'success' });
      setShowRollbackModal(false);
      await loadQuestion();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await adminFetch(`/api/admin/content-qa/questions/${questionId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note_text: newNote }),
      });
      setNewNote('');
      await loadQuestion();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!data || !editedQ) return null;

  const { qa, translations, versions, notes } = data;
  const q = editedQ;
  const qaStatus: QAStatus = qa.qa_status || 'live';

  const transMap: Record<string, any> = {};
  (translations || []).forEach((t: any) => {
    transMap[t.language_code] = t;
  });

  const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'content', label: 'Inhalt', icon: <Edit3 size={14} /> },
    { key: 'image', label: 'Bild', icon: <ImageIcon size={14} /> },
    { key: 'translations', label: 'Sprachen', icon: <Globe size={14} /> },
    { key: 'history', label: 'Verlauf', icon: <History size={14} /> },
    { key: 'preview', label: 'Vorschau', icon: <Smartphone size={14} /> },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ArrowLeft size={16} />
        </button>
        <span className="badge badge-neutral" style={{ fontSize: 12 }}>
          Kap. {q.chapter_id}
        </span>
        <QAStatusBadge status={qaStatus} size="md" />
        {qa.change_markers?.map((m: string) => (
          <ChangeMarkerBadge key={m} marker={m} />
        ))}
        <span className="text-xs text-muted" style={{ marginLeft: 'auto' }}>
          ID: {q.id.substring(0, 8)}...
        </span>
      </div>

      {/* Message */}
      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span>{message.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div
        className="flex gap-1 mb-5"
        style={{ borderBottom: '2px solid var(--border)', paddingBottom: 0 }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`btn btn-sm ${activeTab === tab.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab(tab.key)}
            style={{ borderRadius: '8px 8px 0 0', fontSize: 12, padding: '8px 16px' }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Split Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* Left: Tab Content */}
        <div>
          {activeTab === 'content' && <ContentEditor q={q} setQ={setEditedQ} />}

          {activeTab === 'image' && (
            <ImageTab
              questionId={questionId}
              question={data.question}
              onRefresh={loadQuestion}
              setMessage={setMessage}
            />
          )}

          {activeTab === 'translations' && (
            <TranslationView question={data.question} translations={translations} />
          )}

          {activeTab === 'history' && (
            <HistoryView versions={versions} onRollback={handleRollback} />
          )}

          {activeTab === 'preview' && <MobilePreview question={data.question} />}
        </div>

        {/* Right: Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Publishing Actions */}
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Workflow</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving}
                style={{ width: '100%' }}
              >
                {saving ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Save size={14} />
                )}
                Speichern
              </button>
              {(qaStatus === 'in_progress' ||
                qaStatus === 'draft' ||
                qaStatus === 'updated' ||
                qaStatus === 'in_review') && (
                <button
                  className="btn btn-sm"
                  onClick={handleApprove}
                  style={{
                    width: '100%',
                    background: 'rgba(34,197,94,0.12)',
                    color: '#22C55E',
                    border: '1px solid rgba(34,197,94,0.3)',
                  }}
                >
                  <CheckCircle size={14} /> Freigeben
                </button>
              )}
              {(qaStatus === 'approved' ||
                qaStatus === 'in_progress' ||
                qaStatus === 'updated') && (
                <button
                  className="btn btn-sm"
                  onClick={handlePublish}
                  style={{
                    width: '100%',
                    background: 'rgba(5,150,105,0.12)',
                    color: '#059669',
                    border: '1px solid rgba(5,150,105,0.3)',
                  }}
                >
                  <Send size={14} /> Live schalten
                </button>
              )}
              {versions.length > 1 && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowRollbackModal(true)}
                  style={{ width: '100%' }}
                >
                  <RotateCcw size={14} /> Rollback
                </button>
              )}
            </div>
          </div>

          {/* Translation Status */}
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Übersetzungen</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(LANG_INFO).map(([code, info]) => {
                if (code === 'de')
                  return (
                    <div key={code} className="flex items-center gap-2" style={{ fontSize: 12 }}>
                      <span>{info.flag}</span>
                      <span style={{ flex: 1, fontWeight: 600 }}>{info.label}</span>
                      <TranslationDot status="synced" />
                    </div>
                  );
                const ts = transMap[code];
                return (
                  <div key={code} className="flex items-center gap-2" style={{ fontSize: 12 }}>
                    <span>{info.flag}</span>
                    <span style={{ flex: 1 }}>{info.label}</span>
                    <TranslationDot status={ts?.status || 'outdated'} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
              <MessageSquare size={12} /> Notizen ({notes.length})
            </h4>
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
              {notes.length === 0 ? (
                <div className="text-xs text-muted">Keine Notizen.</div>
              ) : (
                notes.map((note: any) => (
                  <div
                    key={note.id}
                    style={{
                      padding: '6px 0',
                      borderBottom: '1px solid var(--border)',
                      fontSize: 11,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span style={{ fontWeight: 600 }}>{note.author}</span>
                      <span className="text-muted">
                        {new Date(note.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                    <div style={{ marginTop: 2, color: 'var(--text-secondary)' }}>
                      {note.note_text}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <input
                className="input"
                placeholder="Notiz..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                style={{ fontSize: 11, flex: 1 }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
              />
              <button
                className="btn btn-sm btn-secondary"
                onClick={handleAddNote}
                disabled={!newNote.trim()}
              >
                <Send size={10} />
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="card" style={{ padding: 16, fontSize: 11 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Metadaten</h4>
            <div className="flex flex-col gap-1 text-muted">
              {qa.last_approved_by && (
                <div>
                  Freigegeben: {qa.last_approved_by} (
                  {qa.last_approved_at
                    ? new Date(qa.last_approved_at).toLocaleDateString('de-DE')
                    : '—'}
                  )
                </div>
              )}
              {qa.last_published_by && (
                <div>
                  Veröffentlicht: {qa.last_published_by} (
                  {qa.last_published_at
                    ? new Date(qa.last_published_at).toLocaleDateString('de-DE')
                    : '—'}
                  )
                </div>
              )}
              <div>Versionen: {versions.length}</div>
              <div>Fehlerpunkte: {q.points}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Rollback Modal */}
      {showRollbackModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowRollbackModal(false)}
        >
          <div
            className="card"
            style={{ padding: 24, width: 500, maxHeight: '70vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 16 }}>Rollback — Version wählen</h3>
            {versions
              .filter((v) => v.change_type !== 'initial')
              .map((v: any) => {
                const typeConfig = CHANGE_TYPE_ICONS[v.change_type] || CHANGE_TYPE_ICONS.edit;
                return (
                  <div
                    key={v.id}
                    className="flex items-center gap-3"
                    style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 13, minWidth: 30 }}>
                      v{v.version_number}
                    </span>
                    <span
                      style={{
                        color: typeConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                      }}
                    >
                      {typeConfig.icon} {typeConfig.label}
                    </span>
                    <span className="text-xs text-muted" style={{ flex: 1 }}>
                      {v.change_summary}
                    </span>
                    <span className="text-xs text-muted">
                      {new Date(v.created_at).toLocaleDateString('de-DE')}
                    </span>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => handleRollback(v.version_number)}
                    >
                      <RotateCcw size={12} /> Wiederherstellen
                    </button>
                  </div>
                );
              })}
            <button
              className="btn btn-secondary"
              onClick={() => setShowRollbackModal(false)}
              style={{ marginTop: 16 }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Content Editor Tab ───────────────────────────────────────────────
const ContentEditor: React.FC<{ q: any; setQ: (q: any) => void }> = ({ q, setQ }) => {
  const updateOption = (index: number, text: string) => {
    const opts = [...(q.options_de || [])];
    opts[index] = { ...opts[index], text };
    setQ({ ...q, options_de: opts });
  };

  const toggleCorrect = (optId: string) => {
    const ids = q.correct_option_ids.includes(optId)
      ? q.correct_option_ids.filter((id: string) => id !== optId)
      : [...q.correct_option_ids, optId];
    setQ({ ...q, correct_option_ids: ids });
  };

  return (
    <div>
      {/* Chapter + Difficulty + Type */}
      <div className="grid-cols-3 mb-4">
        <div>
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Kapitel
          </label>
          <select
            className="input"
            value={q.chapter_id}
            onChange={(e) => setQ({ ...q, chapter_id: Number(e.target.value) })}
          >
            {CHAPTERS.map((ch) => (
              <option key={ch} value={ch}>
                Kapitel {ch}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Schwierigkeit
          </label>
          <select
            className="input"
            value={q.difficulty}
            onChange={(e) => setQ({ ...q, difficulty: e.target.value })}
          >
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Typ
          </label>
          <select
            className="input"
            value={q.type}
            onChange={(e) => setQ({ ...q, type: e.target.value })}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Points */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Fehlerpunkte
        </label>
        <input
          className="input"
          type="number"
          min={2}
          max={5}
          value={q.points}
          onChange={(e) => setQ({ ...q, points: Number(e.target.value) })}
          style={{ width: 80 }}
        />
      </div>

      {/* Question Text */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          🇩🇪 Fragetext (Master)
        </label>
        <textarea
          className="input"
          rows={3}
          value={q.question_text_de || ''}
          onChange={(e) => setQ({ ...q, question_text_de: e.target.value })}
        />
      </div>

      {/* Options */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Antwortoptionen
        </label>
        <div className="flex flex-col gap-2">
          {(q.options_de || []).map((opt: any, i: number) => (
            <div key={opt.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={q.correct_option_ids.includes(opt.id)}
                onChange={() => toggleCorrect(opt.id)}
                title="Korrekte Antwort"
              />
              <span className="text-xs" style={{ fontWeight: 600, width: 20 }}>
                {opt.id})
              </span>
              <input
                className="input"
                value={opt.text}
                onChange={(e) => updateOption(i, e.target.value)}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted mt-2">Checkbox = korrekte Antwort</p>
      </div>

      {/* Explanation */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Erklärung
        </label>
        <textarea
          className="input"
          rows={2}
          value={q.explanation_de || ''}
          onChange={(e) => setQ({ ...q, explanation_de: e.target.value })}
        />
      </div>

      {/* Visual Prompt */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Bild-Prompt
        </label>
        <input
          className="input"
          value={q.visual_prompt || ''}
          onChange={(e) => setQ({ ...q, visual_prompt: e.target.value })}
          placeholder="z.B. Kreuzung mit Vorfahrtsschild rechts..."
        />
      </div>

      {/* Image Preview */}
      {q.image_url && (
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Aktuelles Bild
          </label>
          <img
            src={q.image_url}
            alt="Fragebild"
            style={{
              maxWidth: '100%',
              maxHeight: 200,
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          />
        </div>
      )}

      {/* Tags */}
      <div className="mb-4">
        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Tags
        </label>
        <input
          className="input"
          value={(q.tags || []).join(', ')}
          onChange={(e) =>
            setQ({
              ...q,
              tags: e.target.value
                .split(',')
                .map((t: string) => t.trim())
                .filter(Boolean),
            })
          }
          placeholder="tag1, tag2, ..."
        />
      </div>
    </div>
  );
};

// ── Image Tab with Generation + Versioning ──────────────────────────
const ImageTab: React.FC<{
  questionId: string;
  question: any;
  onRefresh: () => void;
  setMessage: (m: { text: string; type: 'success' | 'error' } | null) => void;
}> = ({ questionId, question, onRefresh, setMessage }) => {
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState(question.visual_prompt || '');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/content-qa/questions/${questionId}/images`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const d = await resp.json();
      setVersions(d.versions || []);
      if (d.current_prompt && !prompt) setPrompt(d.current_prompt);
    } catch (e: any) {
      console.error('Load versions error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [questionId]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setMessage({ text: 'Bitte einen Bild-Prompt eingeben.', type: 'error' });
      return;
    }
    setGenerating(true);
    setMessage(null);
    try {
      const resp = await adminFetch(
        `/api/admin/content-qa/questions/${questionId}/generate-image`,
        {
          method: 'POST',
          body: JSON.stringify({ prompt: prompt.trim() }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const result = await resp.json();
      setMessage({ text: `Bild generiert (v${result.version}) ✨`, type: 'success' });
      await loadVersions();
      onRefresh();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleRollback = async (versionId: string, versionNumber: number) => {
    if (!confirm(`Bild auf Version ${versionNumber} zurücksetzen?`)) return;
    try {
      const resp = await adminFetch(
        `/api/admin/content-qa/questions/${questionId}/rollback-image`,
        {
          method: 'POST',
          body: JSON.stringify({ version_id: versionId }),
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      setMessage({
        text: `Bild v${result.restored_version} wiederhergestellt ⏪`,
        type: 'success',
      });
      await loadVersions();
      onRefresh();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    }
  };

  const activeVersion = versions.find((v) => v.is_active);

  return (
    <div>
      {/* Current Image */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
          <ImageIcon size={18} style={{ color: 'var(--primary)' }} />
          <h4 style={{ fontSize: 14, fontWeight: 700 }}>Aktuelles Bild</h4>
          {activeVersion && (
            <span className="badge badge-neutral" style={{ fontSize: 10 }}>
              v{activeVersion.version_number}
            </span>
          )}
        </div>
        {question.image_url ? (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={question.image_url}
              alt="Aktuelles Fragebild"
              style={{
                maxWidth: '100%',
                maxHeight: 320,
                borderRadius: 12,
                border: '2px solid var(--border)',
                cursor: 'pointer',
              }}
              onClick={() => setPreviewUrl(question.image_url)}
            />
            <div className="text-xs text-muted" style={{ marginTop: 8 }}>
              Klicken zum Vergrößern
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              borderRadius: 12,
              border: '2px dashed var(--border)',
              background: 'var(--bg-secondary)',
            }}
          >
            <ImageIcon size={40} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
            <div className="text-muted" style={{ fontSize: 13 }}>
              Kein Bild vorhanden
            </div>
            <div className="text-xs text-muted">Generiere unten ein neues Bild mit KI</div>
          </div>
        )}
      </div>

      {/* Generate New Image */}
      <div
        className="card"
        style={{
          padding: 20,
          marginBottom: 20,
          border: '1px solid rgba(139,92,246,0.2)',
          background: 'rgba(139,92,246,0.03)',
        }}
      >
        <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
          <Wand2 size={18} style={{ color: '#8B5CF6' }} />
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#8B5CF6' }}>Neues Bild generieren</h4>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
            Visual Prompt (beschreibt das gewünschte Bild)
          </label>
          <textarea
            className="input"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="z.B. Kreuzung mit Vorfahrtsschild rechts, zwei Autos warten an der Haltelinie, Blick aus Cockpitperspektive, deutsche Straßenverhältnisse..."
            style={{ fontSize: 13 }}
            disabled={generating}
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            className="btn btn-sm"
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            style={{
              background: generating
                ? 'var(--bg-secondary)'
                : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
              color: '#fff',
              border: 'none',
              padding: '8px 20px',
              opacity: generating || !prompt.trim() ? 0.6 : 1,
            }}
          >
            {generating ? (
              <>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Wird generiert...
              </>
            ) : (
              <>
                <Wand2 size={14} /> Bild generieren
              </>
            )}
          </button>
          {generating && (
            <span
              className="text-xs text-muted"
              style={{ animation: 'pulse 2s ease-in-out infinite' }}
            >
              ⏳ Dies kann 10–30 Sekunden dauern...
            </span>
          )}
        </div>
      </div>

      {/* Version History */}
      <div className="card" style={{ padding: 20 }}>
        <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
          <History size={18} style={{ color: 'var(--text-muted)' }} />
          <h4 style={{ fontSize: 14, fontWeight: 700 }}>Bildversionen ({versions.length})</h4>
          <button
            className="btn btn-ghost btn-sm"
            onClick={loadVersions}
            style={{ marginLeft: 'auto' }}
          >
            <RotateCw size={12} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-sm text-muted" style={{ textAlign: 'center', padding: 20 }}>
            Keine Bildversionen vorhanden.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 12,
            }}
          >
            {versions.map((v) => (
              <div
                key={v.id}
                style={{
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: v.is_active ? '2px solid #22C55E' : '1px solid var(--border)',
                  background: 'var(--surface)',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    height: 120,
                    overflow: 'hidden',
                    background: 'var(--bg-secondary)',
                  }}
                  onClick={() => setPreviewUrl(v.image_url)}
                >
                  <img
                    src={v.image_url}
                    alt={`v${v.version_number}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    loading="lazy"
                  />
                  {v.is_active && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        background: '#22C55E',
                        color: '#fff',
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      AKTIV
                    </div>
                  )}
                  <div
                    style={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      background: 'rgba(0,0,0,0.7)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    v{v.version_number}
                  </div>
                </div>
                {/* Info */}
                <div style={{ padding: '8px 10px' }}>
                  <div className="text-xs text-muted" style={{ marginBottom: 2 }}>
                    {new Date(v.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  <div
                    className="text-xs"
                    style={{
                      fontWeight: 500,
                      marginBottom: 6,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    von {v.generated_by}
                  </div>
                  {!v.is_active && (
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => handleRollback(v.id, v.version_number)}
                      style={{ width: '100%', fontSize: 11, padding: '4px 8px' }}
                    >
                      <RotateCcw size={10} /> Wiederherstellen
                    </button>
                  )}
                  {v.is_active && (
                    <div
                      style={{
                        textAlign: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#22C55E',
                        padding: '4px 8px',
                      }}
                    >
                      ✅ Aktuell
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full-Size Preview Modal */}
      {previewUrl && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setPreviewUrl(null)}
        >
          <div
            style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 12 }}
            />
            <button
              className="btn btn-sm"
              onClick={() => setPreviewUrl(null)}
              style={{
                position: 'absolute',
                top: -12,
                right: -12,
                background: 'var(--surface)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
};

// ── Translation View Tab ─────────────────────────────────────────────
const TranslationView: React.FC<{ question: any; translations: any[] }> = ({
  question,
  translations,
}) => {
  const [viewLang, setViewLang] = useState('en');
  const transMap: Record<string, any> = {};
  (translations || []).forEach((t) => {
    transMap[t.language_code] = t;
  });

  const langCodes = Object.keys(LANG_INFO).filter((c) => c !== 'de');
  const ts = transMap[viewLang];

  return (
    <div>
      {/* Language Tabs */}
      <div
        className="flex gap-1 mb-4"
        style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8 }}
      >
        <span
          style={{
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--primary)',
            background: 'rgba(59,130,246,0.08)',
            borderRadius: 6,
          }}
        >
          🇩🇪 Master
        </span>
        {langCodes.map((code) => {
          const info = LANG_INFO[code];
          const t = transMap[code];
          return (
            <button
              key={code}
              className={`btn btn-sm ${viewLang === code ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setViewLang(code)}
              style={{ fontSize: 11, position: 'relative' }}
            >
              {info.flag} {code.toUpperCase()}
              <span
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background:
                    t?.status === 'synced' || t?.status === 'manually_reviewed'
                      ? '#22C55E'
                      : t?.status === 'auto_translated'
                        ? '#3B82F6'
                        : t?.status === 'translation_error'
                          ? '#EF4444'
                          : '#F59E0B',
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Side-by-Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Master (DE) */}
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: '1px solid rgba(59,130,246,0.3)',
            background: 'rgba(59,130,246,0.03)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>🇩🇪 Master (Deutsch)</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Fragetext:
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                padding: '8px 10px',
                background: 'var(--bg-secondary)',
                borderRadius: 6,
              }}
            >
              {question.question_text_de}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Optionen:
            </div>
            {(question.options_de || []).map((opt: any) => (
              <div
                key={opt.id}
                style={{
                  fontSize: 12,
                  padding: '4px 8px',
                  marginBottom: 2,
                  borderRadius: 4,
                  background: question.correct_option_ids?.includes(opt.id)
                    ? 'rgba(34,197,94,0.1)'
                    : 'var(--bg-secondary)',
                  color: question.correct_option_ids?.includes(opt.id) ? '#22C55E' : undefined,
                  fontWeight: question.correct_option_ids?.includes(opt.id) ? 600 : 400,
                }}
              >
                {opt.id}) {opt.text}
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Erklärung:
            </div>
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                padding: '8px 10px',
                background: 'var(--bg-secondary)',
                borderRadius: 6,
              }}
            >
              {question.explanation_de || '—'}
            </div>
          </div>
        </div>

        {/* Translation */}
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: `1px solid ${ts?.status === 'translation_error' ? 'rgba(239,68,68,0.3)' : 'var(--border)'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              {LANG_INFO[viewLang]?.flag} {LANG_INFO[viewLang]?.label}
            </span>
            {ts && <TranslationBadge status={ts.status} />}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Fragetext:
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                padding: '8px 10px',
                background: 'var(--bg-secondary)',
                borderRadius: 6,
              }}
            >
              {question[`question_text_${viewLang}`] || (
                <span className="text-muted">Nicht übersetzt</span>
              )}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Optionen:
            </div>
            {(question[`options_${viewLang}`] || question.options_de || []).map((opt: any) => (
              <div
                key={opt.id}
                style={{
                  fontSize: 12,
                  padding: '4px 8px',
                  marginBottom: 2,
                  borderRadius: 4,
                  background: 'var(--bg-secondary)',
                }}
              >
                {opt.id}) {opt.text}
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
              Erklärung:
            </div>
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                padding: '8px 10px',
                background: 'var(--bg-secondary)',
                borderRadius: 6,
              }}
            >
              {question[`explanation_${viewLang}`] || (
                <span className="text-muted">Nicht übersetzt</span>
              )}
            </div>
          </div>

          {ts?.last_translated_at && (
            <div className="text-xs text-muted" style={{ marginTop: 10 }}>
              Letzte Übersetzung: {new Date(ts.last_translated_at).toLocaleDateString('de-DE')} (
              {ts.translation_source === 'auto' ? 'Automatisch' : 'Manuell'})
            </div>
          )}
          {ts?.error_message && (
            <div className="text-xs" style={{ marginTop: 6, color: '#EF4444' }}>
              ⚠️ Fehler: {ts.error_message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── History Tab ──────────────────────────────────────────────────────
const HistoryView: React.FC<{ versions: any[]; onRollback: (v: number) => void }> = ({
  versions,
  onRollback,
}) => (
  <div>
    <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
      Versionsverlauf ({versions.length} Einträge)
    </h4>
    <div style={{ position: 'relative', paddingLeft: 24 }}>
      {/* Timeline line */}
      <div
        style={{
          position: 'absolute',
          left: 7,
          top: 0,
          bottom: 0,
          width: 2,
          background: 'var(--border)',
        }}
      />
      {versions.map((v: any) => {
        const typeConfig = CHANGE_TYPE_ICONS[v.change_type] || CHANGE_TYPE_ICONS.edit;
        return (
          <div key={v.id} style={{ position: 'relative', paddingBottom: 16 }}>
            {/* Dot */}
            <div
              style={{
                position: 'absolute',
                left: -20,
                top: 3,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: typeConfig.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid var(--surface)',
              }}
            >
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
            </div>
            <div
              style={{ padding: '4px 12px', borderRadius: 8, background: 'var(--bg-secondary)' }}
            >
              <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>v{v.version_number}</span>
                <span
                  style={{
                    color: typeConfig.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 11,
                  }}
                >
                  {typeConfig.icon} {typeConfig.label}
                </span>
                <span className="text-xs text-muted" style={{ marginLeft: 'auto' }}>
                  {new Date(v.created_at).toLocaleString('de-DE')}
                </span>
              </div>
              <div className="text-xs text-muted">{v.change_summary || '—'}</div>
              <div className="text-xs text-muted">von {v.changed_by}</div>
              {v.changed_fields?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {v.changed_fields.map((f: string) => (
                    <span
                      key={f}
                      style={{
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 3,
                        background: 'rgba(59,130,246,0.1)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ── Mobile Preview Tab ──────────────────────────────────────────────
const MobilePreview: React.FC<{ question: any }> = ({ question }) => {
  const [previewLang, setPreviewLang] = useState('de');
  const text = question[`question_text_${previewLang}`] || question.question_text_de;
  const options = question[`options_${previewLang}`] || question.options_de || [];
  const explanation = question[`explanation_${previewLang}`] || question.explanation_de;

  return (
    <div>
      <div className="flex gap-1 mb-4">
        {Object.entries(LANG_INFO).map(([code, info]) => (
          <button
            key={code}
            className={`btn btn-sm ${previewLang === code ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPreviewLang(code)}
            style={{ fontSize: 11 }}
          >
            {info.flag}
          </button>
        ))}
      </div>

      {/* Mobile Frame */}
      <div
        style={{
          width: 375,
          margin: '0 auto',
          borderRadius: 28,
          overflow: 'hidden',
          border: '3px solid var(--border)',
          background: 'var(--bg)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        {/* Status Bar */}
        <div
          style={{
            padding: '8px 16px',
            background: 'var(--surface)',
            fontSize: 10,
            textAlign: 'center',
            fontWeight: 600,
          }}
        >
          Fahryo — App Vorschau
        </div>

        {/* Content */}
        <div style={{ padding: 20 }}>
          {/* Chapter Badge */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 6,
                background: 'rgba(59,130,246,0.12)',
                color: '#3B82F6',
              }}
            >
              Kapitel {question.chapter_id}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 6,
                background: 'rgba(245,158,11,0.12)',
                color: '#F59E0B',
              }}
            >
              {question.points} Punkte
            </span>
          </div>

          {/* Image */}
          {question.image_url && (
            <img
              src={question.image_url}
              alt="Frage"
              style={{
                width: '100%',
                borderRadius: 12,
                marginBottom: 16,
                border: '1px solid var(--border)',
              }}
            />
          )}

          {/* Question Text */}
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, marginBottom: 16 }}>
            {text}
          </div>

          {/* Options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((opt: any) => {
              const isCorrect = question.correct_option_ids?.includes(opt.id);
              return (
                <div
                  key={opt.id}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    fontSize: 14,
                    border: `2px solid ${isCorrect ? '#22C55E' : 'var(--border)'}`,
                    background: isCorrect ? 'rgba(34,197,94,0.08)' : 'var(--surface)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isCorrect ? '#22C55E' : 'var(--bg-secondary)',
                      color: isCorrect ? '#fff' : undefined,
                    }}
                  >
                    {opt.id}
                  </span>
                  <span style={{ fontWeight: isCorrect ? 600 : 400 }}>{opt.text}</span>
                </div>
              );
            })}
          </div>

          {/* Explanation */}
          {explanation && (
            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(59,130,246,0.06)',
                border: '1px solid rgba(59,130,246,0.15)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, color: '#3B82F6' }}>
                💡 Erklärung
              </div>
              {explanation}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
